import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Column,
  PrimaryBlueButton,
  SecondaryGrayButton,
  TableBase
} from "@velaro/velaro-component-lib";
import BulkActionBar from "@/components/BulkActionBar";
import ActionBar from "@/pages/Messaging/ChatWindow/ActionBar";
import AddFieldModal from "./AddFieldModal";
import EditFieldModal from "./EditFieldModal";

export interface FieldOption {
  label: string;
  value: string;
  id: string;
}

export interface FieldDefinition {
  type: "text" | "select" | "checkbox" | "textarea";
  options: Array<FieldOption>;
  label: string;
  scope: "workflow" | "conversation" | "contact";
  persistent: boolean;
  description: string;
}

export interface ContactFieldDef {
  id: number;
  name: string;
  label: string;
  type: string;
  options?: string;
  sortOrder: number;
  isRequired: boolean;
  scope?: string;
  persistent?: boolean;
  description?: string;
}

const scopeLabels: Record<string, string> = {
  workflow: "Workflow",
  conversation: "Conversation",
  contact: "Contact"
};

const typeLabels: Record<string, string> = {
  text: "Text",
  checkbox: "Checkbox",
  textarea: "Textarea",
  select: "Select List"
};

const columns: Column[] = [
  { label: "Label", accessor: "label" },
  { label: "Variable", accessor: "variable" },
  { label: "Type", accessor: "type" },
  { label: "Scope", accessor: "scope" },
  { label: "Persistent", accessor: "persistent" },
  { label: "", accessor: "actions", disableSort: true }
];

type SubTab = "fields" | "reference";

export default function CustomFields() {
  const api = useApi();
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [editFieldId, setEditFieldId] = useState<number | undefined>();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<ContactFieldDef[]>([]);
  const [maxFields, setMaxFields] = useState(10);
  const [copiedName, setCopiedName] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("fields");
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  useEffect(() => {
    api.messaging
      .get("ContactFieldDefinitions/list")
      .then(async (result) => {
        if (result.ok) {
          const data = await result.json();
          if (Array.isArray(data)) {
            setFields(data);
          } else {
            setFields(
              Array.isArray(data?.definitions) ? data.definitions : []
            );
            if (data?.maxCustomFields) {
              setMaxFields(data.maxCustomFields);
            }
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [api.messaging]);

  const removeField = useCallback(
    async (id: number) => {
      if (!window.confirm("Are you sure you want to delete this field?")) {
        return;
      }
      try {
        const resp = await api.messaging.delete(
          `ContactFieldDefinitions/${id}`
        );
        if (resp.ok) {
          setFields((prev) => prev.filter((f) => f.id !== id));
          setSelectedIndices([]);
        }
      } catch {
        // Keep existing data on failure
      }
    },
    [api.messaging]
  );

  async function bulkDelete() {
    if (
      !window.confirm(
        `Delete ${selectedIndices.length} selected field${selectedIndices.length > 1 ? "s" : ""}?`
      )
    ) {
      return;
    }
    const ids = selectedIndices.map((i) => fields[i]?.id).filter(Boolean);
    for (const id of ids) {
      try {
        await api.messaging.delete(`ContactFieldDefinitions/${id}`);
      } catch {
        // Continue deleting others
      }
    }
    setFields((prev) => prev.filter((f) => !ids.includes(f.id)));
    setSelectedIndices([]);
  }

  const submit = useCallback(
    async (definition: FieldDefinition) => {
      setShowAddFieldModal(false);

      let optionsJson: string | undefined;
      if (
        definition.type === "select" &&
        Array.isArray(definition.options) &&
        definition.options.length > 0
      ) {
        optionsJson = JSON.stringify(
          definition.options.map((o) => o.value || o.label)
        );
      }

      try {
        const resp = await api.messaging.post("ContactFieldDefinitions", {
          name: definition.label.toLowerCase().replace(/\s+/g, "_"),
          label: definition.label,
          type: definition.type,
          options: optionsJson,
          sortOrder: 0,
          isRequired: false,
          scope: definition.scope ?? "conversation",
          persistent: definition.persistent ?? false,
          description: definition.description ?? ""
        });

        if (resp.ok) {
          const created: ContactFieldDef = await resp.json();
          setFields((prev) => [...prev, created]);
        }
      } catch {
        // Keep existing data on failure
      }
    },
    [api.messaging]
  );

  const handleUpdate = useCallback(
    async (id: number, definition: FieldDefinition) => {
      setEditFieldId(undefined);

      let optionsJson: string | undefined;
      if (
        definition.type === "select" &&
        Array.isArray(definition.options) &&
        definition.options.length > 0
      ) {
        optionsJson = JSON.stringify(
          definition.options.map((o) => o.value || o.label)
        );
      }

      try {
        const resp = await api.messaging.put(`ContactFieldDefinitions/${id}`, {
          name: definition.label.toLowerCase().replace(/\s+/g, "_"),
          label: definition.label,
          type: definition.type,
          options: optionsJson,
          sortOrder: 0,
          isRequired: false,
          scope: definition.scope ?? "conversation",
          persistent: definition.persistent ?? false,
          description: definition.description ?? ""
        });

        if (resp.ok) {
          setFields((prev) =>
            prev.map((f) =>
              f.id === id
                ? {
                    ...f,
                    label: definition.label,
                    name: definition.label
                      .toLowerCase()
                      .replace(/\s+/g, "_"),
                    type: definition.type,
                    options: optionsJson,
                    scope: definition.scope,
                    persistent: definition.persistent,
                    description: definition.description
                  }
                : f
            )
          );
        }
      } catch {
        // Keep existing data on failure
      }
    },
    [api.messaging]
  );

  function copyVariable(name: string) {
    navigator.clipboard?.writeText(`<<${name}>>`);
    setCopiedName(name);
    setTimeout(() => setCopiedName(null), 2000);
  }

  const tableData = useMemo(() => {
    return fields.map((field) => ({
      label: (
        <div>
          <span className="font-medium">{field.label}</span>
          {field.description && (
            <p className="text-xs text-gray-400 mt-0.5">{field.description}</p>
          )}
        </div>
      ),
      variable: (
        <button
          className="bg-gray-100 hover:bg-cornflower-blue-100 px-2 py-0.5 rounded text-xs font-mono transition-colors"
          title={`Click to copy <<${field.name}>>`}
          aria-label={`Copy variable ${field.name}`}
          onClick={() => copyVariable(field.name)}
        >
          {"<<"}
          {field.name}
          {">>"}
          {copiedName === field.name && (
            <i
              className="fa fa-check text-green-600 ml-1"
              aria-hidden="true"
            />
          )}
        </button>
      ),
      type: typeLabels[field.type] ?? field.type,
      scope: (
        <span className="inline-block bg-gray-100 text-xs px-2 py-0.5 rounded">
          {scopeLabels[field.scope ?? "conversation"] ??
            field.scope ??
            "Conversation"}
        </span>
      ),
      persistent: field.persistent ? (
        <i className="fa fa-check text-green-600" aria-label="Persistent" />
      ) : (
        <i className="fa fa-minus text-gray-300" aria-label="Not persistent" />
      ),
      actions: (
        <ActionBar
          onEdit={() => setEditFieldId(field.id)}
          onCopy={() => copyVariable(field.name)}
          onDelete={() => removeField(field.id)}
        />
      )
    }));
  }, [fields, copiedName, removeField]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const atLimit = maxFields > 0 && fields.length >= maxFields;

  const subTabClass = (tab: SubTab) =>
    `pb-2 text-sm font-medium border-b-2 whitespace-nowrap ${
      activeSubTab === tab
        ? "border-blue-500 text-blue-600"
        : "border-transparent text-gray-500 hover:text-gray-700"
    }`;

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold">Custom Fields & Variables</h2>
          <span className="text-sm text-gray-500">
            {fields.length} / {maxFields} fields used
          </span>
        </div>
        {activeSubTab === "fields" && (
          <div className="ml-auto">
            <PrimaryBlueButton
              label="Add Field"
              disabled={atLimit}
              onClick={() => !atLimit && setShowAddFieldModal(true)}
            />
          </div>
        )}
      </div>

      {/* Sub-tab navigation */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex gap-6">
          <button className={subTabClass("fields")} onClick={() => setActiveSubTab("fields")}>
            Manage Fields
          </button>
          <button className={subTabClass("reference")} onClick={() => setActiveSubTab("reference")}>
            Variable Reference
          </button>
        </div>
      </div>

      {activeSubTab === "fields" && (
        <>
          {atLimit && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
              Maximum of {maxFields} custom fields reached. Remove unused fields or
              upgrade your plan to add more.
            </div>
          )}

          {/* Bulk Action Bar */}
          {selectedIndices.length > 0 && (
            <div className="mb-4">
              <BulkActionBar>
                <div>
                  ({selectedIndices.length}){" "}
                  {selectedIndices.length === 1 ? "field" : "fields"} selected
                </div>
                <SecondaryGrayButton onClick={bulkDelete} label="Delete Selected" />
              </BulkActionBar>
            </div>
          )}

          {/* Fields Table */}
          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 border border-gray-200 rounded-lg bg-white">
              <i className="fa fa-database text-3xl mb-3" aria-hidden="true" />
              <p className="font-medium text-gray-500">No custom fields defined</p>
              <p className="text-sm mt-1">
                Click &quot;Add Field&quot; to create your first variable.
              </p>
            </div>
          ) : (
            <div className="overflow-auto flex-1">
              <TableBase
                columns={columns}
                data={tableData}
                onChangeSelection={setSelectedIndices}
              />
            </div>
          )}
        </>
      )}

      {activeSubTab === "reference" && (
        <div className="overflow-y-auto flex-1 space-y-6">
          {/* How to use */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-800 mb-1">How to use variables</p>
            <p className="text-xs text-blue-700">
              Use the <code className="bg-white px-1 py-0.5 rounded text-blue-800 font-mono">{"<<variable.key>>"}</code> syntax
              anywhere variables are accepted: bot messages, workflow conditions, AI system prompts,
              canned responses, and API webhook payloads.
            </p>
          </div>

          {/* Terminology */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-1">Terminology</p>
            <div className="text-xs text-gray-600 space-y-0.5">
              <p><strong>Custom Fields</strong> = the field definitions (schema) you configure in the Manage Fields tab</p>
              <p><strong>Attributes</strong> = the actual data values stored on contacts and conversations</p>
              <p><strong>Variables</strong> = the <code className="bg-white px-1 rounded font-mono">{"<<contact.custom.X>>"}</code> reference syntax used in bots &amp; workflows</p>
            </div>
          </div>

          {/* Embed Setup */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Embed Setup</h4>
            <p className="text-xs text-gray-600 mb-2">
              Pass metadata from your website by adding a <code className="bg-gray-100 px-1 rounded font-mono">customData</code> object
              to <code className="bg-gray-100 px-1 rounded font-mono">window.__velaro</code>. Each key-value pair becomes a conversation and contact attribute.
            </p>
            <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto font-mono leading-relaxed">{`<script>
  window.__velaro = {
    siteId: "your-site-id",
    deploymentId: "your-deployment-id",
    customData: {
      accountId: "ACCT-789",
      planTier: "enterprise",
      pageContext: "billing-settings",
      _language: "fr",       // underscore prefix = internal (hidden)
      _referrer: "google"    // stripped to "referrer", scope=internal
    }
  };
</script>
<script src="https://messaging.velaro.com/embed.js"></script>`}</pre>
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <p className="text-xs text-amber-800">
                <strong>Important:</strong> Set <code className="font-mono">customData</code> before <code className="font-mono">embed.js</code> loads.
                The widget reads it once at initialization.
              </p>
            </div>
          </div>

          {/* Macro Syntax */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Macro Syntax</h4>
            <p className="text-xs text-gray-600 mb-2">
              Variables can be referenced using these syntax options:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-[11px] text-gray-400 uppercase tracking-wide">
                    <th className="px-3 py-1.5">Syntax</th>
                    <th className="px-3 py-1.5">Example</th>
                    <th className="px-3 py-1.5">When to Use</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {[
                    ["<<key>>", "<<accountId>>", "Default — workflows, bot messages, email templates"],
                    ["{{key}}", "{{accountId}}", "Alternative curly-brace syntax"],
                    ["[[key]]", "[[planTier]]", "When angle/curly braces conflict with templating"],
                    ["((key))", "((caseNumber))", "In parenthesis-heavy text"],
                    ["${key}", "${userId}", "JavaScript-style messages"],
                  ].map(([syntax, example, when]) => (
                    <tr key={syntax} className="border-b border-gray-50">
                      <td className="px-3 py-1.5 font-mono font-medium">{syntax}</td>
                      <td className="px-3 py-1.5 font-mono">{example}</td>
                      <td className="px-3 py-1.5 text-gray-500">{when}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Dot-Notation Access */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Dot-Notation Access</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 text-[11px] text-gray-400 uppercase tracking-wide">
                    <th className="px-3 py-1.5">Path</th>
                    <th className="px-3 py-1.5">Resolves To</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {[
                    ["contact.email", "Contact's email address"],
                    ["contact.firstName", "Contact's first name"],
                    ["contact.custom.myField", "Custom attribute 'myField' on contact"],
                    ["conversation.source", "Channel (web, sms, ivr, whatsapp)"],
                    ["conversation.id", "Conversation ID"],
                    ["conversation.city", "Visitor's city (geo-IP)"],
                    ["conversation.country", "Visitor's country"],
                    ["workflow.myVar", "Workflow-scoped variable"],
                  ].map(([path, desc]) => (
                    <tr key={path} className="border-b border-gray-50">
                      <td className="px-3 py-1.5">
                        <button
                          type="button"
                          onClick={() => copyVariable(path)}
                          className="font-mono text-blue-600 hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors cursor-pointer"
                          title="Click to copy"
                          aria-label={`Copy ${path}`}
                        >
                          {`<<${path}>>`}
                        </button>
                      </td>
                      <td className="px-3 py-1.5 text-gray-500">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Variable Scopes */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Variable Scopes</h4>
            <div className="space-y-3">
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block bg-cornflower-blue-100 text-cornflower-blue-700 text-xs font-semibold px-2 py-0.5 rounded">Contact</span>
                  <span className="text-sm font-medium">Saved forever with the customer</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Persists across all conversations. Use for profile data like name, email, company, customer tier, language preference.
                </p>
                <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                  <span className="text-gray-400">{"// Set in AskQuestion or SetData node with "}</span>
                  <span className="text-cornflower-blue-500">Contact Attribute</span>
                  <span className="text-gray-400"> toggled on</span><br />
                  <span className="text-gray-400">{"// Then use in Send Message: "}</span><br />
                  Hello <span className="text-cornflower-blue-500">{"<<contact.firstName>>"}</span>, welcome back! Your tier is <span className="text-cornflower-blue-500">{"<<contact.customer_tier>>"}</span>.
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">Conversation</span>
                  <span className="text-sm font-medium">Shared across all workflows in this chat</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Available for the entire conversation but cleared when the conversation closes. Use for ticket numbers, issue category, routing decisions.
                </p>
                <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                  <span className="text-gray-400">{"// Workflow 1 sets the ticket via SetData node:"}</span><br />
                  ticket_id = <span className="text-green-600">&quot;TKT-12345&quot;</span><br />
                  <span className="text-gray-400">{"// Workflow 2 (transfer notification) can read it:"}</span><br />
                  Transferring chat for ticket <span className="text-cornflower-blue-500">{"<<conversation.ticket_id>>"}</span>
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded">Workflow</span>
                  <span className="text-sm font-medium">Temporary, isolated to this workflow run</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Only exists while the workflow is executing. Cleared when the workflow finishes. Use for temporary calculations, loop counters, API response data.
                </p>
                <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                  <span className="text-gray-400">{"// SetData: store API result temporarily"}</span><br />
                  api_status = <span className="text-green-600">&quot;approved&quot;</span><br />
                  <span className="text-gray-400">{"// Condition node checks it, then it's gone after the workflow completes"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resolution Priority */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Resolution Priority</h4>
            <p className="text-xs text-gray-500 mb-2">
              When a variable name exists in multiple scopes, the system checks in this order (first match wins):
            </p>
            <div className="flex gap-2 text-xs">
              <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">1. Workflow</span>
              <span className="text-gray-400 self-center">&rarr;</span>
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">2. Conversation</span>
              <span className="text-gray-400 self-center">&rarr;</span>
              <span className="bg-cornflower-blue-100 text-cornflower-blue-700 px-2 py-1 rounded font-medium">3. Contact</span>
              <span className="text-gray-400 self-center">&rarr;</span>
              <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded font-medium">4. Built-in</span>
            </div>
          </div>

          {/* Workflow Condition Comparators */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Workflow Condition Comparators</h4>
            <p className="text-xs text-gray-600 mb-2">
              Custom variables can be evaluated in workflow condition nodes using these comparators:
            </p>
            <div className="overflow-x-auto max-h-64 overflow-y-auto">
              <table className="w-full text-left">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-gray-200 text-[11px] text-gray-400 uppercase tracking-wide">
                    <th className="px-3 py-1.5">Comparator</th>
                    <th className="px-3 py-1.5">Description</th>
                    <th className="px-3 py-1.5">Example</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {[
                    ["equals", "Case-insensitive equality", 'planTier equals "enterprise"'],
                    ["notEquals", "Case-insensitive inequality", 'status notEquals "cancelled"'],
                    ["contains", "Value contains substring", 'tags contains "vip"'],
                    ["notContains", "Value does not contain substring", 'email notContains "spam"'],
                    ["startsWith", "Value starts with prefix", 'accountId startsWith "ACCT-"'],
                    ["endsWith", "Value ends with suffix", 'email endsWith "@company.com"'],
                    ["greaterThan", "Numeric >", 'cartTotal greaterThan "100"'],
                    ["lessThan", "Numeric <", 'trialDaysLeft lessThan "7"'],
                    ["between", "Numeric range (min,max)", 'cartTotal between "50,500"'],
                    ["exists", "Value is not null/empty", "accountId exists"],
                    ["notExists", "Value is null/empty", "promoCode notExists"],
                    ["regex", "Matches regular expression", 'phone regex "^\\+1[0-9]{10}$"'],
                    ["inList", "Value in comma-separated list", 'planTier inList "pro,enterprise"'],
                    ["notInList", "Not in comma-separated list", 'region notInList "blocked"'],
                  ].map(([comp, desc, ex]) => (
                    <tr key={comp} className="border-b border-gray-50">
                      <td className="px-3 py-1.5 font-mono font-medium text-blue-700">{comp}</td>
                      <td className="px-3 py-1.5 text-gray-600">{desc}</td>
                      <td className="px-3 py-1.5 text-gray-400 font-mono">{ex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Transforms available: <code className="font-mono bg-gray-100 px-1 rounded">lowercase</code>,{" "}
              <code className="font-mono bg-gray-100 px-1 rounded">uppercase</code>,{" "}
              <code className="font-mono bg-gray-100 px-1 rounded">trim</code>,{" "}
              <code className="font-mono bg-gray-100 px-1 rounded">length</code>.
              Conditions can be grouped with AND/OR logic.
            </p>
          </div>

          {/* Where Variables Work */}
          <div>
            <h4 className="font-semibold text-sm mb-2">Where Variables Work</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fa fa-comment text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium">Send Message</span>
                </div>
                <p className="text-xs text-gray-500 mb-1">Personalize chatbot responses with stored data.</p>
                <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block">
                  Hi <span className="text-cornflower-blue-500">{"<<firstName>>"}</span>, how can I help?
                </code>
              </div>
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fa fa-code-branch text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium">Condition Nodes</span>
                </div>
                <p className="text-xs text-gray-500 mb-1">Branch workflows based on variable values.</p>
                <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block">
                  If <span className="text-cornflower-blue-500">customer_tier</span> equals &quot;premium&quot;
                </code>
              </div>
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fa fa-globe text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium">HTTP Request</span>
                </div>
                <p className="text-xs text-gray-500 mb-1">Pass variables in API request bodies.</p>
                <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block whitespace-pre">
                  {`{ "email": "`}<span className="text-cornflower-blue-500">{"<<email>>"}</span>{`" }`}
                </code>
              </div>
              <div className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <i className="fa fa-envelope text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium">Email Nodes</span>
                </div>
                <p className="text-xs text-gray-500 mb-1">Include variables in subject and body.</p>
                <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded block">
                  Re: Ticket <span className="text-cornflower-blue-500">{"<<ticket_id>>"}</span>
                </code>
              </div>
            </div>
          </div>

          {/* How Variables Get Set */}
          <div>
            <h4 className="font-semibold text-sm mb-2">How Variables Get Set</h4>
            <div className="space-y-4">
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fa fa-database text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium text-sm">Set Data Node</span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  Manually set name/value pairs. Each pair becomes a variable you can reference later.
                </p>
                <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                  <span className="text-gray-400">{"// Example: Tag a conversation and store an API result"}</span><br />
                  <span className="text-green-600">department</span> = <span className="text-cornflower-blue-500">&quot;billing&quot;</span>
                  <span className="text-gray-400 ml-2">{"// Conversation scope"}</span><br />
                  <span className="text-green-600">priority</span> = <span className="text-cornflower-blue-500">&quot;high&quot;</span>
                  <span className="text-gray-400 ml-2">{"// Conversation scope"}</span><br />
                  <span className="text-green-600">customer_tier</span> = <span className="text-cornflower-blue-500">&quot;enterprise&quot;</span>
                  <span className="text-gray-400 ml-2">{"// Contact scope (persists)"}</span>
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fa fa-question-circle text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium text-sm">Ask Question Node</span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  Asks the visitor a question and automatically saves their reply as a variable.
                </p>
                <div className="bg-white rounded p-2 text-xs font-mono text-gray-600">
                  <span className="text-gray-400">{"// Example: Collect visitor email"}</span><br />
                  Question: <span className="text-cornflower-blue-500">&quot;What&apos;s your email address?&quot;</span><br />
                  Type: <span className="text-green-600">email</span><br />
                  Save as: <span className="text-green-600">email</span> with Contact scope ON<br />
                  <span className="text-gray-400">{"// Now <<email>> and <<contact.email>> work everywhere"}</span>
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fa fa-user text-cornflower-blue-500" aria-hidden="true" />
                  <span className="font-medium text-sm">Built-in Fields</span>
                </div>
                <p className="text-xs text-gray-600 mb-1">
                  Always available without setup. Populated from the contact record and embed/pre-chat data.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {["name", "firstName", "lastName", "email", "phone", "language"].map((f) => (
                    <code key={f} className="bg-white border px-1.5 py-0.5 rounded text-xs font-mono text-gray-600">
                      {"<<"}{f}{">>"}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Examples */}
          <div>
            <h4 className="font-semibold text-sm mb-3">Workflow Examples</h4>
            <div className="space-y-4">
              <div className="border rounded-lg p-3 bg-gray-50">
                <p className="text-xs font-semibold text-gray-700 mb-2">Support Ticket Routing</p>
                <div className="text-xs space-y-1.5">
                  {[
                    ['Ask Question: "What\'s your email?" → Save as email (Contact)'],
                    ['Ask Question: "What do you need help with?" (Multiple choice) → Save as issue_type (Conversation)'],
                    ['Set Data: ticket_id = "TKT-" + timestamp (Conversation)'],
                    ['Condition: If issue_type = "Technical" → Transfer to AI'],
                    ['Transfer to AI: AI receives all variables automatically'],
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">{i + 1}</span>
                      <span className="text-gray-600">{step[0]}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-gray-50">
                <p className="text-xs font-semibold text-gray-700 mb-2">E-commerce Order Lookup</p>
                <div className="text-xs space-y-1.5">
                  {[
                    ['Ask Question: "What\'s your order number?" → Save as order_id (Conversation)'],
                    ["HTTP Request: GET https://api.store.com/orders/<<order_id>>"],
                    ["Set Data: order_status = response value (Conversation)"],
                    ['Send Message: "Order <<order_id>> status: <<order_status>>"'],
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">{i + 1}</span>
                      <span className="text-gray-600">{step[0]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Usage Examples with dark code blocks */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">Usage Examples</p>
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-gray-500 block mb-0.5">Bot greeting message:</span>
                <code className="block bg-gray-900 text-gray-100 rounded px-2 py-1.5 font-mono">
                  {"Hi <<contact.firstName>>, how can I help you today?"}
                </code>
              </div>
              <div>
                <span className="text-gray-500 block mb-0.5">Workflow condition (check VIP):</span>
                <code className="block bg-gray-900 text-gray-100 rounded px-2 py-1.5 font-mono">
                  {'<<contact.custom.vip_status>> == "true"'}
                </code>
              </div>
              <div>
                <span className="text-gray-500 block mb-0.5">AI system prompt context:</span>
                <code className="block bg-gray-900 text-gray-100 rounded px-2 py-1.5 font-mono">
                  {"The customer's name is <<contact.name>> from <<contact.company>>. Their account ID is <<contact.custom.account_id>>."}
                </code>
              </div>
              <div>
                <span className="text-gray-500 block mb-0.5">Webhook payload:</span>
                <code className="block bg-gray-900 text-gray-100 rounded px-2 py-1.5 font-mono">
                  {'{ "email": "<<contact.email>>", "dept": "<<contact.custom.department>>" }'}
                </code>
              </div>
            </div>
          </div>

          {/* Quick Copy — your fields */}
          {fields.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">Your Variables (click to copy)</h4>
              <div className="flex flex-wrap gap-1.5">
                {fields.map((f) => (
                  <button
                    key={f.id}
                    className="bg-gray-100 hover:bg-cornflower-blue-100 px-2 py-1 rounded text-xs font-mono transition-colors"
                    title={`Click to copy <<${f.name}>>`}
                    aria-label={`Copy variable ${f.name}`}
                    onClick={() => copyVariable(f.name)}
                  >
                    {"<<"}{f.name}{">>"}
                    {copiedName === f.name && (
                      <i className="fa fa-check text-green-600 ml-1" aria-hidden="true" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showAddFieldModal && (
        <AddFieldModal
          show={showAddFieldModal}
          onCancel={() => setShowAddFieldModal(false)}
          submit={submit}
        />
      )}

      {editFieldId != null && (
        <EditFieldModal
          field={fields.find((f) => f.id === editFieldId)!}
          onCancel={() => setEditFieldId(undefined)}
          onSave={(def) => handleUpdate(editFieldId, def)}
        />
      )}
    </div>
  );
}
